import { localize } from "client_legacy/ndaw/culture";
import {
	ActionAndOthers,
	ActionBarItemAlignment,
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { navigateToOwa } from "client/Features/Email/OwaNavigation/owaNavigator";
import { EmailActionModel } from "./selectionActions";

export type EmailAppActionContext = {
	isTouch: boolean;
	isMobile: boolean;
	isInSelectMode: boolean;
	hasSession: boolean;
	selectedItems: readonly EmailActionModel[];
};

export type EmailAppAction = ActionAndOthers<EmailAppActionContext>;

export type EmailAppActionHandlers = {
	composeNewEmail(): void;
	enableSelectMode(): void;
	disableSelectMode(): void;
	changeSelectAll(selectAll: boolean): void;
};

type Options = {
	showGoToOutlookAction: boolean;
	handlers: EmailAppActionHandlers;
};

class EmailAppActions {
	private readonly _openOwa: EmailAppAction;
	private readonly _enableSelectMode: EmailAppAction;
	private readonly _disableSelectMode: EmailAppAction;
	private readonly _selectAll: EmailAppAction;
	private readonly _deselectAll: EmailAppAction;
	private readonly _deselectAllDivider: EmailAppAction;

	constructor(options: Options) {
		const { handlers, showGoToOutlookAction } = options;

		this._enableSelectMode = {
			kind: ActionKind.Regular,
			name: "enableSelectMode",
			title: localize("_Select"),
			icon: "hand-pointer",
			canExecute: ctx => ctx.isMobile && !ctx.isInSelectMode && !ctx.hasSession,
			execute: () => handlers.enableSelectMode()
		};

		this._disableSelectMode = {
			kind: ActionKind.Regular,
			name: "disableSelectMode",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: ctx => ctx.isMobile && ctx.isInSelectMode && !ctx.hasSession,
			execute: () => handlers.disableSelectMode()
		};

		this._openOwa = {
			kind: ActionKind.Regular,
			name: "openAdvancedEmail",
			title: localize("_OpenAdvanceEmail"),
			icon: "share-from-square",
			canExecute: () => showGoToOutlookAction,
			execute: () => navigateToOwa(),
			align: ActionBarItemAlignment.right,
		};

		this._selectAll = {
			kind: ActionKind.Regular,
			name: "selectAll",
			title: localize("_SelectAll"),
			icon: "circle-check",
			canExecute: ctx =>
				ctx.isMobile
				&& ctx.isInSelectMode
				&& !ctx.hasSession,
			execute: () => handlers.changeSelectAll(true)
		};

		this._deselectAll = {
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (ctx) => deselectAllTitle(ctx.selectedItems),
			icon: "circle-xmark",
			canExecute: c => c.selectedItems.length > 0
				&& (!c.isMobile || (c.isInSelectMode && !c.hasSession)),
			execute: () => handlers.changeSelectAll(false)
		};

		this._deselectAllDivider = {
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		};
	}

	public getAllActions(): EmailAppAction[] {
		const actions: EmailAppAction[] = [
			this._enableSelectMode,
			this._disableSelectMode,
			this._deselectAll,
			this._deselectAllDivider,
			this._selectAll,
			this._openOwa
		];

		return actions;
	}
}

export {
	EmailAppActions
};
